/**
 * B2B product page: option-aware variant matrix, sample modal, stock counter,
 * and locale-aware Ajax Cart submission.
 */
;(() => {
  const B2B = window.B2B
  const { withCountry, fmtMoney } = B2B
  const bundleId = B2B.bundleId
  const parseJson = B2B.parseJsonScript
  const ADD_URL = B2B.urls.add
  const CART_JSON_URL = B2B.urls.cartJson
  const UPDATE_URL = B2B.urls.update
  const CART_URL = B2B.urls.cart

  // Per-product tiers only; the global ladder arrives already shaped from
  // `b2b-bulk-pricing`. These may carry an absolute `price`, so they need it.
  const shapeProductTiers = (tiers) =>
    (tiers || [])
      .map((tier) => ({
        min: Number(tier.min),
        price: tier.price == null ? null : Number(tier.price),
        discount: tier.discount == null ? null : Number(tier.discount),
        save: tier.save == null ? null : Number(tier.save),
      }))
      .filter(
        (tier) =>
          Number.isFinite(tier.min) &&
          tier.min > 1 &&
          ((Number.isFinite(tier.price) && tier.price > 0) ||
            (Number.isFinite(tier.discount) && tier.discount > 0) ||
            (Number.isFinite(tier.save) && tier.save > 0))
      )
      .sort((a, b) => a.min - b.min)

  const readTiers = (scope) => {
    const el =
      scope.querySelector("[data-bulk-tiers]") ||
      scope.parentElement?.querySelector("[data-bulk-tiers]")
    const data = parseJson(el, { base: 0, tiers: [] })
    const globalPricing = window.BaerskinB2B?.bulkPricing
    const useGlobal = data.useGlobal !== false && globalPricing

    data.tiers = useGlobal
      ? globalPricing.config.tiers
      : shapeProductTiers(data.tiers)
    return data
  }

  const priceForTier = (base, tier) => {
    if (!tier) {
      return Number(base) || 0
    }
    if (Number.isFinite(tier.price) && tier.price > 0) {
      return tier.price
    }
    const discount = Number(tier.discount ?? tier.save)
    const globalPricing = window.BaerskinB2B?.bulkPricing
    if (Number.isFinite(discount) && globalPricing) {
      return globalPricing.priceForDiscount(base, discount)
    }
    // No local percentage arithmetic: without the shared ladder module, list
    // price is the only figure that matches what checkout charges.
    return Number(base) || 0
  }

  /**
   * An absolute-`price` tier is already a unit price, so it multiplies. The
   * percentage helper settles a whole-cent unit price: do not re-round after it.
   */
  const lineTotalForTier = (base, qty, tier) => {
    const quantity = Number(qty) || 0
    const absolute = Number(tier?.price)
    const discount = Number(tier?.discount ?? tier?.save)
    const globalPricing = window.BaerskinB2B?.bulkPricing

    // An absolute tier price is already a unit price, so it multiplies.
    if (Number.isFinite(absolute) && absolute > 0) {
      return quantity * absolute
    }

    if (Number.isFinite(discount) && globalPricing) {
      return globalPricing.lineTotalForDiscount(base, quantity, discount)
    }

    // No tier, or no shared module to ask: list price, same as priceForTier.
    return quantity * priceForTier(base, tier)
  }

  const saveForTier = (base, tier) => {
    if (!tier) {
      return 0
    }
    const explicitSave = Number(tier.discount ?? tier.save)
    if (Number.isFinite(explicitSave)) {
      return explicitSave
    }
    const price = priceForTier(base, tier)
    return Math.max(0, Math.round(((base - price) * 100) / base))
  }

  const pickTier = (data, total) => {
    if (!data.tiers.length) {
      return null
    }
    let active = null
    for (const t of data.tiers) {
      if (total >= t.min) {
        active = t
      }
    }
    return active
  }

  const rows = (scope) =>
    Array.from(scope.querySelectorAll("[data-size-row]")).filter(
      (row) => !row.hidden
    )

  const SIZE_RANKS = new Map(
    [
      "xxs",
      "xs",
      "s",
      "m",
      "l",
      "xl",
      "2xl",
      "3xl",
      "4xl",
      "5xl",
      "6xl",
      "7xl",
      "8xl",
    ].map((size, index) => [size, index])
  )

  // Exact colour names from PIN (hexColor attribute and colour registry); tokens below catch the rest.
  const NAME_SWATCHES = {
    "air force blue": "#abb8d0",
    "alpine green": "#5ed9ab",
    "amber": "#6b3f37",
    "arctic white": "#ded0c8",
    "army green": "#615634",
    "atlantic navy":
      "linear-gradient(135deg, #ffffff 0% 33%, #abb8d0 33% 67%, #172d4e 67% 100%)",
    "beige": "#f6ece2",
    "black": "#000000",
    "black charcoal melange": "#161717",
    "black mountain": "#1a1c1c",
    "blue": "#72779e",
    "blue m\u00e9lange": "#677c9d",
    "bottle green": "#367174",
    "bottle green & navy":
      "linear-gradient(135deg, #367174 0% 50%, #172d4e 50% 100%)",
    "bourbon": "#916e56",
    "bourbon brown": "#964b00",
    "brown": "#e0ab79",
    "burgundy": "#661624",
    "burgundy & black": "#6e1b35",
    "burgundy red": "#550915",
    "burnt orange": "#e85d25",
    "camo":
      "repeating-linear-gradient(135deg, #33412a 0 10px, #6b5b38 10px 20px, #1f2419 20px 30px)",
    "charcoal grey": "#726d71",
    "charcoal melange": "#d7d8dd",
    "cream": "#ddd6c6",
    "dark green": "#48514d",
    "dark green camo":
      "repeating-linear-gradient(135deg, #1f2e1c 0 10px, #3d4f2c 10px 20px, #121a10 20px 30px)",
    "dark grey": "#808080",
    "deep red": "#f64048",
    "desert brown": "#d7996c",
    "dusty pink": "#d38d8b",
    "fatigue green": "#666344",
    "fresh lilac": "#e4c3e4",
    "glacier blue": "#19577b",
    "green": "#8a8957",
    "green & black": "#494737",
    "grey": "#5f6367",
    "grey & black": "#615e67",
    "grey & white": "#5f6066",
    "grey black": "#4a4749",
    "grey camo":
      "repeating-linear-gradient(135deg, #6b6f73 0 10px, #9aa0a6 10px 20px, #3d4044 20px 30px)",
    "grey/white": "#aeaeae",
    "grizzly brown": "#46332c",
    "grizzly brown & stone":
      "linear-gradient(135deg, #46332c 0% 50%, #f3dfc9 50% 100%)",
    "hi viz orange": "#f8974b",
    "hi viz yellow": "#f6e365",
    "hunter green": "#484836",
    "hunter orange": "#ff5f1f",
    "hunting camo":
      "repeating-linear-gradient(135deg, #33412a 0 10px, #6b5b38 10px 20px, #1f2419 20px 30px)",
    "ice blue": "#6bd2f0",
    "ice sheet grey": "#e6ebec",
    "indigo": "#313b4c",
    "indigo blue": "#6f81a8",
    "khaki": "#c4b289",
    "lagoon teal":
      "linear-gradient(135deg, #2f506c 0% 33%, #6598a9 33% 67%, #eceaea 67% 100%)",
    "light blue": "#96c7d1",
    "light green": "#81a68b",
    "light grey": "#eceaea",
    "lilac": "#bf9fc6",
    "marine blue": "#6598a9",
    "mid blue": "#4a7bb8",
    "midnight teal": "#2f506c",
    "midnight teal & black":
      "linear-gradient(135deg, #2f506c 0% 50%, #1c1c1c 50% 100%)",
    "military camo":
      "repeating-linear-gradient(135deg, #33412a 0 10px, #6b5b38 10px 20px, #1f2419 20px 30px)",
    "multi cam black":
      "repeating-linear-gradient(135deg, #111111 0 10px, #2b2b2b 10px 20px, #444444 20px 30px)",
    "multicam":
      "repeating-linear-gradient(135deg, #33412a 0 10px, #6b5b38 10px 20px, #1f2419 20px 30px)",
    "navy": "#13193f",
    "navy & grey": "#1f2936",
    "navy blue": "#172d4e",
    "navy camo":
      "repeating-linear-gradient(135deg, #1d2a44 0 10px, #3b4a66 10px 20px, #0f1626 20px 30px)",
    "navy mountain": "#2d2d3d",
    "navy white stripe": "#3d3a4e",
    "ocean teal": "#2583a4",
    "ocean teal & black": "#265c85",
    "ochre": "#dcb16d",
    "od green camo":
      "repeating-linear-gradient(135deg, #4b5320 0 10px, #6b6b3a 10px 20px, #2f3416 20px 30px)",
    "olive green": "#808000",
    "orange": "#ffa500",
    "pink": "#bf89a0",
    "pink melange": "#dca3b1",
    "pink m\u00e9lange": "#dca3b1",
    "red": "#a22130",
    "red & navy": "linear-gradient(135deg, #ed2737 0% 50%, #172d4e 50% 100%)",
    "rust": "#bb3216",
    "sage green": "#b4bb99",
    "sage green melange": "#afbcb2",
    "sage green m\u00e9lange": "#afbcb2",
    "sahara stone": "#f3dfc9",
    "ski blue": "#093791",
    "ski blue & black":
      "linear-gradient(135deg, #093791 0% 50%, #1c1c1c 50% 100%)",
    "ski red": "#ed2737",
    "snow camo":
      "repeating-linear-gradient(135deg, #e9ecef 0 10px, #b8bec4 10px 20px, #7d858c 20px 30px)",
    "stainless steel": "#b0b5b8",
    "stealth black": "#1c1c1c",
    "stealth black camo":
      "repeating-linear-gradient(135deg, #111111 0 10px, #2b2b2b 10px 20px, #444444 20px 30px)",
    "steel grey": "#838891",
    "stone": "#bdad86",
    "stone & navy": "#e7d7b9",
    "teal": "#244660",
    "teal green": "#0f766e",
    "terracotta": "#ca825b",
    "tortoise shell": "#805a45",
    "usa black": "#000000",
    "usa blue": "#4aa3c8",
    "usa blue line": "#14569c",
    "usa bronze": "#b79478",
    "usa od green": "#948f7f",
    "usa red": "#d71928",
    "usa silver": "#848184",
    "white": "#ffffff",
    "white grey": "#6b6a6f",
    "white grey melange": "#535458",
    "white navy stripe": "#f7f0e5",
    "wildberry burgundy":
      "linear-gradient(135deg, #661624 0% 33%, #bf9fc6 33% 67%, #ffffff 67% 100%)",
    "yellow": "#f0d890",
    // No hex in PIN for these: picked by eye.
    "arctic dusk": "#6f7f99",
    "desert sage": "#9caf88",
    "dusty rose": "#c9969a",
    "gold": "#c9a227",
    "peach": "#f4b89a",
    "purple": "#6a3d8f",
    "rose": "#d47a8a",
    "turquoise": "#2ab3b1",
    "blue melange": "#5b7fa8",
    "burgundy black":
      "linear-gradient(135deg, #5a1725 0% 50%, #111111 50% 100%)",
    "camo green":
      "repeating-linear-gradient(135deg, #33412a 0 10px, #6b5b38 10px 20px, #1f2419 20px 30px)",
    "chocolate brown": "#5c3a21",
    "cobalt blue": "#0047ab",
    "dark navy": "#0f1a33",
    "dusty blue": "#7a96b5",
    "green black": "linear-gradient(135deg, #4b5320 0% 50%, #111111 50% 100%)",
    "navy airforce blue ice grey":
      "linear-gradient(135deg, #14294a 0% 33%, #5d8aa8 33% 67%, #c9d2d8 67% 100%)",
    "navy grey": "linear-gradient(135deg, #14294a 0% 50%, #8f8f8f 50% 100%)",
    "navy melange": "#2c3a57",
    "neon orange": "#ff5f1f",
    "petrol blue": "#1f4e5f",
    "red green": "linear-gradient(135deg, #c5242f 0% 50%, #3f6f3f 50% 100%)",
    "tapioca beige": "#e3d5bd",
    "teal blue": "#2a6f7f",
    "teal blue melange": "#4a7f8a",
    "wine red": "#722f37",
    "winter white": "#f2efe6",
  }

  const COLOR_TOKENS = [
    ["charcoal", "#36454f"],
    ["dusty pink", "#d4a5a5"],
    ["pink", "#e8a0b4"],
    ["lilac", "#c8a2c8"],
    ["indigo", "#3f3d73"],
    ["khaki", "#b5a77a"],
    ["beige", "#d9c8a9"],
    ["cream", "#f3ead2"],
    ["ochre", "#c38a2c"],
    ["amber", "#d18b1f"],
    ["terracotta", "#c26a4a"],
    ["bourbon", "#7b4a25"],
    ["bronze", "#9c6b3c"],
    ["silver", "#c0c0c0"],
    [
      "tortoise",
      "radial-gradient(circle at 30% 30%, #c98a3d 0 25%, #5a3417 26% 60%, #2b170a 61%)",
    ],
    [
      "multicolor",
      "conic-gradient(#c5242f, #f47a20, #d7f000, #3f6f3f, #14294a, #c5242f)",
    ],
    ["stealth black", "#111111"],
    ["black", "#111111"],
    ["navy blue", "#14294a"],
    ["navy", "#14294a"],
    ["marine blue", "#1f5f8f"],
    ["air force blue", "#5d8aa8"],
    ["ski blue", "#1d9bd1"],
    ["glacier blue", "#9fc8df"],
    ["blue", "#2d6f9f"],
    ["ocean teal", "#0b7f83"],
    ["midnight teal", "#0e4d5d"],
    ["teal", "#0b7f83"],
    ["hunter green", "#35583a"],
    ["army green", "#4b5320"],
    ["bottle green", "#1f4d2b"],
    ["green", "#3f6f3f"],
    ["sahara stone", "#c7b89a"],
    ["desert brown", "#8b6f47"],
    ["grizzly brown", "#6b4f35"],
    ["bourbon brown", "#7b4a25"],
    ["brown", "#6b4f35"],
    ["stone", "#c7b89a"],
    ["steel grey", "#71797e"],
    ["ice sheet grey", "#c9d2d8"],
    ["grey", "#8f8f8f"],
    ["gray", "#8f8f8f"],
    ["white", "#f7f7f2"],
    ["hi viz yellow", "#d7f000"],
    ["yellow", "#d7f000"],
    ["hi viz orange", "#ff7a00"],
    ["burnt orange", "#c75b12"],
    ["orange", "#f47a20"],
    ["ski red", "#c5242f"],
    ["red", "#c5242f"],
    ["burgundy", "#5a1725"],
    ["rust", "#9a4b2f"],
    [
      "camo",
      "repeating-linear-gradient(135deg, #33412a 0 10px, #6b5b38 10px 20px, #1f2419 20px 30px)",
    ],
  ]

  const colorParts = (name) => {
    const normalized = String(name || "")
      .toLowerCase()
      .replace(/\+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
    const parts = normalized.split(/\s*(?:&|\/|\band\b|\+)\s*/).filter(Boolean)
    return parts.length ? parts : [normalized]
  }

  const swatchColorFor = (part) => {
    if (NAME_SWATCHES[part]) {
      return NAME_SWATCHES[part]
    }
    for (const [token, value] of COLOR_TOKENS) {
      if (part.includes(token)) {
        return value
      }
    }
    return ""
  }

  const fallbackSwatchBackground = (name) => {
    const whole = String(name || "")
      .toLowerCase()
      .replace(/\+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
    if (NAME_SWATCHES[whole]) {
      return NAME_SWATCHES[whole]
    }
    const colors = colorParts(name).map(swatchColorFor).filter(Boolean)
    if (colors.length === 0) {
      return ""
    }
    if (colors.length === 1) {
      return colors[0]
    }
    // A gradient cannot be a colour stop, so a pattern part wins the swatch.
    const pattern = colors.find((color) => color.includes("gradient("))
    if (pattern) {
      return pattern
    }
    const step = 100 / colors.length
    const stops = colors
      .map((color, index) => {
        const from = Math.round(index * step)
        const to = Math.round((index + 1) * step)
        return `${color} ${from}% ${to}%`
      })
      .join(", ")
    return `linear-gradient(135deg, ${stops})`
  }

  const findSwatch = (scope, color) =>
    Array.from(scope.querySelectorAll("[data-swatch]")).find(
      (swatch) => swatch.dataset.swatch === color
    )

  const copySwatchVisual = (target, source, color) => {
    if (!target) {
      return
    }
    target.style.background = ""
    target.style.backgroundImage = ""
    target.style.backgroundSize = ""
    target.style.backgroundPosition = ""
    target.removeAttribute("data-swatch-resolved")
    target.dataset.swatchInitial = (color || "?").trim()[0] || "?"

    if (source && (source.style.background || source.style.backgroundImage)) {
      target.style.background = source.style.background
      target.style.backgroundImage = source.style.backgroundImage
      target.style.backgroundSize = source.style.backgroundSize || "cover"
      target.style.backgroundPosition =
        source.style.backgroundPosition || "center"
      if (source.dataset.swatchResolved) {
        target.dataset.swatchResolved = source.dataset.swatchResolved
      }
      return
    }

    const fallback = fallbackSwatchBackground(color)
    if (fallback) {
      target.style.background = fallback
      target.dataset.swatchResolved = "name"
    } else {
      target.dataset.swatchResolved = "missing"
    }
  }

  const syncSelectedColor = (scope, color) => {
    const name = scope.querySelector("[data-selected-color-name]")
    if (name) {
      name.textContent = color || ""
    }
    copySwatchVisual(
      scope.querySelector("[data-selected-swatch]"),
      findSwatch(scope, color),
      color
    )
  }

  const variantColor = (payload, variant) => {
    const colorIndex = (payload.optionNames || []).findIndex((name) =>
      /^colou?r$/i.test(name)
    )
    if (colorIndex < 0 || !variant || !Array.isArray(variant.options)) {
      return ""
    }
    return variant.options[colorIndex] || ""
  }

  const variantForColor = (payload, color) =>
    (payload.variants || []).find(
      (variant) =>
        variant.available &&
        variant.image &&
        variant.image.url &&
        variantColor(payload, variant) === color
    ) ||
    (payload.variants || []).find(
      (variant) =>
        variant.image &&
        variant.image.url &&
        variantColor(payload, variant) === color
    )

  const updateMainImage = (scope, payload, color) => {
    const image = scope
      .closest(".product")
      ?.querySelector("[data-main-product-image]")
    if (!image) {
      return
    }
    const variant = variantForColor(payload, color)
    if (!variant || !variant.image || !variant.image.url) {
      return
    }
    image.src = variant.image.url
    image.removeAttribute("srcset")
    image.alt = variant.image.alt || payload.title || ""
  }

  const initSwatches = (scope) => {
    for (const swatch of scope.querySelectorAll("[data-swatch]")) {
      swatch.dataset.swatchInitial =
        (swatch.dataset.swatch || "?").trim()[0] || "?"
      if (swatch.style.background || swatch.style.backgroundImage) {
        continue
      }
      const fallback = fallbackSwatchBackground(swatch.dataset.swatch)
      if (fallback) {
        swatch.style.background = fallback
        swatch.dataset.swatchResolved = "name"
      } else {
        swatch.dataset.swatchResolved = "missing"
      }
    }
  }

  const normalizedSize = (value) => {
    const size = String(value || "")
      .toLowerCase()
      .replace(/\b(?:men'?s|women'?s|unisex)\b/g, "")
      .replace(/\bextra\s+extra\s+small\b/g, "xxs")
      .replace(/\bextra\s+small\b/g, "xs")
      .replace(/\bx\s*small\b/g, "xs")
      .replace(/\bsmall\b/g, "s")
      .replace(/\bmedium\b/g, "m")
      .replace(/\bextra\s+large\b/g, "xl")
      .replace(/\bx\s*large\b/g, "xl")
      .replace(/\blarge\b/g, "l")
      .replace(/\b(\d+)\s*x\s*(?:large|l)\b/g, "$1xl")
      .replace(/\bxxxxl\b/g, "4xl")
      .replace(/\bxxxl\b/g, "3xl")
      .replace(/\bxxl\b/g, "2xl")
      .replace(/[^a-z0-9.]+/g, "")
    return size
  }

  const sizeRank = (value) => {
    const normalized = normalizedSize(value)
    if (SIZE_RANKS.has(normalized)) {
      return SIZE_RANKS.get(normalized)
    }
    if (/^\d+(?:\.\d+)?$/.test(normalized)) {
      return 100 + Number(normalized)
    }
    return 10000
  }

  const sortSizeRows = (scope) => {
    for (const group of scope.querySelectorAll(".size-grid__tiles")) {
      const ordered = Array.from(group.querySelectorAll("[data-size-row]"))
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
          const rankDelta =
            sizeRank(a.row.dataset.size) - sizeRank(b.row.dataset.size)
          if (rankDelta !== 0) {
            return rankDelta
          }
          const labelDelta = (a.row.dataset.size || "").localeCompare(
            b.row.dataset.size || "",
            undefined,
            { numeric: true, sensitivity: "base" }
          )
          return labelDelta || a.index - b.index
        })
      for (const { row } of ordered) {
        group.append(row)
      }
    }
  }

  const syncSizeLabels = (scope) => {
    for (const row of scope.querySelectorAll("[data-size-row]")) {
      const label = row.querySelector("[data-size-label]")
      if (label && row.dataset.size) {
        label.textContent = row.dataset.size
      }
    }
  }

  const resetQuantities = (scope) => {
    for (const input of scope.querySelectorAll("[data-qty]")) {
      input.value = "0"
    }
  }

  const setText = (scope, sel, value) => {
    const el = scope.querySelector(sel)
    if (el) {
      el.textContent = value
    }
  }

  // Dedupe preserving order: a one-option product has no size, so the row falls
  // back to `variant.title` and echoes that one value into both label parts.
  const uniqueLabelParts = (parts) => [...new Set(parts.filter(Boolean))]

  // Slash-joined like every other variant label here: a comma would collide with
  // the comma separating variants when the stock alert names more than one.
  const labelForRow = (row) =>
    uniqueLabelParts([
      row.dataset.color,
      row.dataset.fit,
      row.dataset.size,
    ]).join(" / ")

  // Line-property keys are English terms shared with checkout, admin and the
  // order pipe; match case-insensitively so foreign-written lines still match.
  const lineProperty = (properties, name) => {
    const wanted = name.toLowerCase()
    for (const key of Object.keys(properties || {})) {
      if (key.toLowerCase() === wanted) {
        return properties[key]
      }
    }
    return undefined
  }

  const stockLimitForRow = (row) => {
    if (
      !row ||
      row.dataset.stockLimit === "" ||
      row.dataset.stockLimit == null
    ) {
      return null
    }
    const value = Number(row.dataset.stockLimit)
    return Number.isFinite(value) ? Math.max(0, value) : null
  }

  const quantityForRow = (row) =>
    Number(row.querySelector("[data-qty]")?.value) || 0

  const storedQuantitiesKey = (payload) => {
    const productKey = payload?.id || payload?.handle
    return productKey ? `b2b:selected-quantities:${productKey}` : ""
  }

  const localStorageRef = () => {
    try {
      return window.localStorage
    } catch (_e) {
      return null
    }
  }

  const readStoredQuantities = (payload) => {
    const key = storedQuantitiesKey(payload)
    const storage = localStorageRef()
    if (!key || !storage) {
      return {}
    }
    try {
      const value = JSON.parse(storage.getItem(key) || "{}")
      return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {}
    } catch (_e) {
      return {}
    }
  }

  const writeStoredQuantities = (scope, payload) => {
    const key = storedQuantitiesKey(payload)
    const storage = localStorageRef()
    if (!key || !storage) {
      return
    }

    const quantities = {}
    for (const row of scope.querySelectorAll("[data-size-row]")) {
      const qty = quantityForRow(row)
      if (row.dataset.variantId && qty > 0) {
        quantities[row.dataset.variantId] = qty
      }
    }

    try {
      if (Object.keys(quantities).length) {
        storage.setItem(key, JSON.stringify(quantities))
      } else {
        storage.removeItem(key)
      }
    } catch (_e) {
      // Storage can be unavailable in private browsing or locked-down contexts.
    }
  }

  const clearStoredQuantities = (payload) => {
    const key = storedQuantitiesKey(payload)
    const storage = localStorageRef()
    if (!key || !storage) {
      return
    }
    try {
      storage.removeItem(key)
    } catch (_e) {
      // Ignore storage failures; cart submission has already succeeded.
    }
  }

  const restoreStoredQuantities = (scope, payload) => {
    const stored = readStoredQuantities(payload)
    let firstRestoredRow = null

    for (const row of scope.querySelectorAll("[data-size-row]")) {
      const qty = Number(stored[row.dataset.variantId])
      const input = row.querySelector("[data-qty]")
      if (
        !input ||
        input.disabled ||
        !row.dataset.variantId ||
        !Number.isFinite(qty) ||
        qty <= 0
      ) {
        continue
      }
      input.value = String(qty)
      window.B2BQuantityRules?.clamp(input)
      firstRestoredRow ||= row
    }

    return firstRestoredRow
  }

  /**
   * Prefills the matrix from the cart; the local copy is wiped after each add.
   * Fills only boxes still at zero, so a value typed mid-flight survives.
   */
  const hydrateFromCart = async (scope, payload) => {
    const productId = Number(payload?.id)
    if (!Number.isFinite(productId)) {
      return 0
    }

    let cart
    try {
      cart = await getCart(scope)
    } catch (_e) {
      // An unreachable cart leaves the matrix as it is. The buyer can still
      // order, they just do not get the prefill.
      return 0
    }

    // Summed, not read off one line: the same variant can sit on several lines
    // under different bundle ids, and the replace step removes all of them.
    const quantities = new Map()
    for (const item of Array.isArray(cart.items) ? cart.items : []) {
      const properties = item.properties || {}
      const isB2BLine =
        lineProperty(properties, "_channel") ||
        lineProperty(properties, "_b2b_bundle_id")
      // A sample carries the channel marker but is not a wholesale line, so it
      // must not seed a quantity or let a sample-only cart zero-fill the matrix.
      if (
        !isB2BLine ||
        lineProperty(properties, "_sample") ||
        Number(item.product_id) !== productId
      ) {
        continue
      }
      const variantId = String(item.variant_id)
      const qty = Number(item.quantity) || 0
      quantities.set(variantId, (quantities.get(variantId) || 0) + qty)
    }
    if (!quantities.size) {
      return 0
    }

    // The cart is authoritative: named rows take its quantity, the rest zero.
    // A row edited since load is left alone (its keystrokes beat this async fetch).
    let filled = 0
    for (const row of scope.querySelectorAll("[data-size-row]")) {
      const input = row.querySelector("[data-qty]")
      if (
        !input ||
        input.disabled ||
        !row.dataset.variantId ||
        input.dataset.userEdited === "1"
      ) {
        continue
      }
      const qty = quantities.get(row.dataset.variantId)
      const next = Number.isFinite(qty) && qty > 0 ? qty : 0
      if (quantityForRow(row) === next) {
        continue
      }
      input.value = String(next)
      // A programmatic write fires no `change`, so clamp manually: a pre-ceiling
      // cart can carry a quantity the form must not resubmit.
      window.B2BQuantityRules?.clamp(input)
      filled += 1
    }

    if (filled) {
      writeStoredQuantities(scope, payload)
      recalc(scope)
    }
    return filled
  }

  const selectedUnitLabel = (count) => {
    const unit = count === 1 ? "unit" : "units"
    return `${count} ${unit} selected`
  }

  const syncSwatchQuantities = (scope) => {
    const totals = new Map()
    for (const row of scope.querySelectorAll("[data-size-row]")) {
      const color = row.dataset.color
      if (!color) {
        continue
      }
      totals.set(color, (totals.get(color) || 0) + quantityForRow(row))
    }

    for (const swatch of scope.querySelectorAll("[data-swatch]")) {
      const color = swatch.dataset.swatch
      const label = swatch.dataset.swatchLabel || color || "Color"
      const total = totals.get(color) || 0
      const badge = swatch.querySelector("[data-swatch-qty]")

      swatch.toggleAttribute("data-has-qty", total > 0)
      swatch.setAttribute(
        "aria-label",
        total > 0 ? `${label}, ${selectedUnitLabel(total)}` : label
      )
      swatch.title =
        total > 0 ? `${label} (${selectedUnitLabel(total)})` : label
      if (badge) {
        badge.textContent = total > 99 ? "99+" : String(total)
        badge.hidden = total <= 0
      }
    }
  }

  const selectionLabelForRow = (row) =>
    uniqueLabelParts([
      row.dataset.color,
      row.dataset.fit,
      row.dataset.size,
    ]).join(" / ")

  const syncSelectionSummary = (scope) => {
    const panel = scope.querySelector("[data-selection-summary]")
    const list = scope.querySelector("[data-selection-summary-list]")
    if (!panel || !list) {
      return
    }

    const selectedRows = Array.from(scope.querySelectorAll("[data-size-row]"))
      .map((row) => ({ row, qty: quantityForRow(row) }))
      .filter(({ row, qty }) => row.dataset.variantId && qty > 0)

    panel.hidden = !selectedRows.length
    list.textContent = ""
    if (!selectedRows.length) {
      return
    }

    for (const { row, qty } of selectedRows) {
      const item = document.createElement("div")
      const name = document.createElement("span")
      const amount = document.createElement("strong")
      const edit = document.createElement("button")

      item.className = "selection-summary__row"
      name.className = "selection-summary__name"
      amount.className = "selection-summary__qty"
      edit.className = "selection-summary__edit"
      edit.type = "button"
      edit.dataset.editVariant = row.dataset.variantId
      edit.textContent = "Edit"
      edit.setAttribute("aria-label", `Edit ${selectionLabelForRow(row)}`)

      name.textContent =
        selectionLabelForRow(row) || row.dataset.sku || "Variant"
      amount.textContent = `${qty} ${qty === 1 ? "unit" : "units"}`
      item.append(name, amount, edit)
      list.append(item)
    }
  }

  const activeVisibleRow = (scope) => {
    const visibleRows = rows(scope)
    if (!visibleRows.length) {
      return null
    }
    const activeId = scope.dataset.activeVariantId
    return (
      visibleRows.find((row) => row.dataset.variantId === activeId) ||
      visibleRows.find(
        (row) => (Number(row.querySelector("[data-qty]")?.value) || 0) > 0
      ) ||
      visibleRows.find((row) => row.dataset.available === "true") ||
      visibleRows[0]
    )
  }

  const stockUnitLabel = (count) => {
    const unit = count === 1 ? "unit" : "units"
    return `${count} ${unit}`
  }

  const leadTimeFromScope = (scope, kind = "standard") => {
    const extended = kind === "extended"
    const days = extended
      ? scope.dataset.extendedLeadDays || "120"
      : scope.dataset.standardLeadDays || "15"

    return {
      days,
      extended,
      text: `Up to ${days} days`,
    }
  }

  const exceedsStandardLeadAllocation = (row) => {
    const limit = stockLimitForRow(row)
    return limit !== null && quantityForRow(row) > limit
  }

  const leadTimeForRow = (scope, row) =>
    row && exceedsStandardLeadAllocation(row)
      ? leadTimeFromScope(scope, "extended")
      : leadTimeFromScope(scope)

  const leadTimeForScope = (scope) =>
    Array.from(scope.querySelectorAll("[data-size-row]")).some(
      (row) => quantityForRow(row) > 0 && exceedsStandardLeadAllocation(row)
    )
      ? leadTimeFromScope(scope, "extended")
      : leadTimeFromScope(scope)

  const tableCell = (tag, text, className) => {
    const el = document.createElement(tag)
    el.textContent = text
    if (className) {
      el.className = className
    }
    return el
  }

  const syncBulkSavings = (scope, row) => {
    const panel = scope.querySelector("[data-bulk-savings]")
    const body = scope.querySelector("[data-bulk-savings-body]")
    if (!panel || !body || !row) {
      return
    }

    const data = readTiers(scope)
    const base = Number(row.dataset.unitPrice) || Number(data.base) || 0
    const tiers = data.tiers || []

    body.textContent = ""
    panel.hidden = !tiers.length || !base
    if (panel.hidden) {
      return
    }

    const rows = [
      {
        label: panel.dataset.quantityLabel || "Quantity",
        values: tiers.map((tier) => `${tier.min}+`),
      },
      {
        label: panel.dataset.saveLabel || "Save",
        values: tiers.map((tier) => {
          const explicitSave = tier.save == null ? NaN : Number(tier.save)
          const save = Number.isFinite(explicitSave)
            ? explicitSave
            : saveForTier(base, tier)
          return save ? `${save}%` : "-"
        }),
        valueClass: "bulk-savings__save",
      },
      {
        label: panel.dataset.priceLabel || "Price",
        values: tiers.map((tier) =>
          fmtMoney(priceForTier(base, tier), scope.dataset.moneyFormat)
        ),
      },
    ]

    for (const rowData of rows) {
      const tr = document.createElement("tr")
      tr.append(tableCell("th", rowData.label, "bulk-savings__label"))
      for (const value of rowData.values) {
        tr.append(tableCell("td", value, rowData.valueClass || ""))
      }
      body.append(tr)
    }
  }

  const syncProductMeta = (scope, row) => {
    const product = scope.closest(".product")
    if (!product || !row) {
      return
    }

    const price = product.querySelector("[data-product-price]")
    if (price) {
      const template = price.dataset.priceTemplate || "From [[price]]"
      price.textContent = template.replace(
        "[[price]]",
        fmtMoney(row.dataset.unitPrice, scope.dataset.moneyFormat)
      )
    }

    const sku = product.querySelector("[data-product-sku]")
    if (sku) {
      const value =
        uniqueLabelParts([row.dataset.color, row.dataset.size]).join(" / ") ||
        row.dataset.sku ||
        ""
      sku.hidden = !value
      sku.textContent = value
    }
  }

  const syncProductionTime = (scope) => {
    const targets = scope
      .closest(".product")
      ?.querySelectorAll("[data-production-time]")
    if (!targets || !targets.length) {
      return
    }
    const leadTime = leadTimeForScope(scope)
    for (const target of targets) {
      target.textContent = leadTime.extended
        ? target.dataset.extendedText || leadTime.text
        : target.dataset.standardText || leadTime.text
    }
  }

  // Appended after the selected size's own line, never replacing it, and names
  // every over-allocation row so the message cannot go stale as others change.
  const appendOverAllowanceNotice = (alert, rows, scope) => {
    const standard = leadTimeFromScope(scope)
    const extended = leadTimeFromScope(scope, "extended")

    alert.append(
      document.createTextNode(` Over the ${standard.days}-day allowance: `)
    )

    rows.forEach((row, index) => {
      if (index > 0) {
        alert.append(
          document.createTextNode(index === rows.length - 1 ? " and " : ", ")
        )
      }
      const strong = document.createElement("strong")
      strong.textContent = labelForRow(row) || "selected variant"
      alert.append(
        strong,
        document.createTextNode(
          ` (${stockUnitLabel(stockLimitForRow(row) || 0)})`
        )
      )
    })

    alert.append(
      document.createTextNode(
        `. ${rows.length === 1 ? "That moves" : "These move"} to ${extended.text.toLowerCase()}.`
      )
    )
  }

  const syncStockLimitWarnings = (scope) => {
    const extendedRows = []

    for (const row of scope.querySelectorAll("[data-size-row]")) {
      const input = row.querySelector("[data-qty]")
      const extended = !!input && exceedsStandardLeadAllocation(row)

      row.toggleAttribute("data-extended-lead-time", extended)
      if (extended) {
        extendedRows.push(row)
      }
    }

    return { extendedRows }
  }

  const syncStockAlert = (
    scope,
    stockLimitState = syncStockLimitWarnings(scope)
  ) => {
    const alert = scope.querySelector("[data-stock-alert]")
    const row = activeVisibleRow(scope)
    for (const sizeRow of scope.querySelectorAll("[data-size-row]")) {
      sizeRow.toggleAttribute(
        "data-active",
        !!row && sizeRow.dataset.variantId === row.dataset.variantId
      )
    }
    syncProductMeta(scope, row)
    syncBulkSavings(scope, row)
    if (!alert || !row) {
      return
    }
    const overRows = stockLimitState.extendedRows
    const anyOver = overRows.length > 0
    const label = labelForRow(row) || "Selected variant"
    const stockLimit = stockLimitForRow(row)
    const available = row.dataset.available === "true"

    alert.textContent = ""
    alert.hidden = false
    if (anyOver) {
      alert.dataset.extendedLeadTime = "true"
    } else {
      delete alert.dataset.extendedLeadTime
    }

    // The selected size always reports its own allowance, even while another
    // size is over.
    if (!available) {
      alert.append(document.createTextNode(`${label} is out of stock.`))
    } else if (stockLimit !== null && stockLimit > 0) {
      const strong = document.createElement("strong")
      strong.textContent = `${stockUnitLabel(stockLimit)} `
      let text = `of ${label} available in ${leadTimeFromScope(scope).text.toLowerCase()}.`
      if (!anyOver) {
        text += ` Higher quantities move to ${leadTimeFromScope(scope, "extended").text.toLowerCase()}.`
      }
      alert.append(strong, document.createTextNode(text))
    } else {
      alert.append(document.createTextNode(`${label} available for order.`))
    }

    if (anyOver) {
      appendOverAllowanceNotice(alert, overRows, scope)
    }
  }

  // Not cleared on success: navigation to the cart is async, and a button
  // re-enabled during that wait takes a second click and orders twice.
  const submitting = new WeakSet()

  const recalc = (scope) => {
    const tierData = readTiers(scope)
    const format = scope.dataset.moneyFormat
    let totalQty = 0
    let subtotal = 0

    for (const row of scope.querySelectorAll("[data-size-row]")) {
      totalQty += quantityForRow(row)
    }

    const tier = pickTier(tierData, totalQty)

    for (const row of scope.querySelectorAll("[data-size-row]")) {
      const qty = quantityForRow(row)
      const baseUnit = Number(row.dataset.unitPrice) || 0
      const line = lineTotalForTier(baseUnit, qty, tier)
      subtotal += line
      const lineEl = row.querySelector("[data-line-total]")
      if (lineEl) {
        lineEl.textContent = qty > 0 ? fmtMoney(line, format) : ""
      }
    }

    const displayBaseUnit =
      Number(activeVisibleRow(scope)?.dataset.unitPrice) ||
      Number(rows(scope)[0]?.dataset.unitPrice) ||
      0
    const displayUnit = tier
      ? priceForTier(displayBaseUnit, tier)
      : displayBaseUnit
    setText(scope, "[data-total-qty]", totalQty)
    setText(scope, "[data-order-unit-price]", fmtMoney(displayUnit, format))
    setText(scope, "[data-grand-total]", fmtMoney(subtotal, format))
    syncSwatchQuantities(scope)
    syncSelectionSummary(scope)
    const stockLimitState = syncStockLimitWarnings(scope)
    syncProductionTime(scope)

    const addBtn = scope.querySelector("[data-add-order]")
    if (addBtn) {
      addBtn.disabled = submitting.has(scope) || totalQty <= 0
    }
    syncStockAlert(scope, stockLimitState)
  }

  const baseProperties = (scope, id) => {
    const props = {}
    const channel = scope.dataset.channel
    if (channel) {
      props._channel = channel
    }
    if (id) {
      props._b2b_bundle_id = id
    }

    return props
  }

  const collectItems = (scope, id) => {
    const props = baseProperties(scope, id)
    const items = []
    for (const row of scope.querySelectorAll("[data-size-row]")) {
      const variantId = row.dataset.variantId
      const qty = quantityForRow(row)
      if (variantId && qty > 0) {
        const itemProps = { ...props }
        itemProps["Lead time"] = leadTimeForRow(scope, row).text
        items.push({
          id: Number(variantId),
          quantity: qty,
          properties: itemProps,
        })
      }
    }
    return items
  }

  const setOrderStatus = (status, message, kind) => {
    if (!status) {
      return
    }
    status.textContent = message
    status.className = "quick-order__status"
    if (kind) {
      status.classList.add(`quick-order__status--${kind}`)
    }
  }

  const cartAddError = async (res) => {
    if (res.status === 401) {
      return new Error(
        "Cart access is blocked by storefront password protection. Unlock the storefront password, then try again."
      )
    }
    const err = await res.json().catch(() => ({}))
    return new Error(
      err.description || err.message || "Could not add to order."
    )
  }

  const getCart = async (scope) => {
    const res = await fetch(
      withCountry(CART_JSON_URL, scope.dataset.checkoutCountry),
      {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      }
    )
    if (!res.ok) {
      throw await cartAddError(res)
    }
    return res.json()
  }

  const updateCartLines = async (scope, updates) => {
    const res = await fetch(
      withCountry(UPDATE_URL, scope.dataset.checkoutCountry),
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ updates }),
      }
    )
    if (!res.ok) {
      throw await cartAddError(res)
    }
    return res.json()
  }

  /**
   * Upserts this product's B2B lines to match the form: existing lines take the
   * new quantity in place, so the cart never holds old and new at once (which
   * trips Shopify's stock cap). Keyed and sparse, so other products' lines survive.
   */
  const upsertProductLines = async (scope, productId, items) => {
    const cart = await getCart(scope)
    const lines = Array.isArray(cart.items) ? cart.items : []
    const wanted = new Map(items.map((item) => [item.id, item]))
    const kept = new Set()
    const updates = {}
    const removed = []
    const original = {}
    const raised = new Map()

    for (const line of lines) {
      const properties = line.properties || {}
      const isB2BLine =
        lineProperty(properties, "_channel") ||
        lineProperty(properties, "_b2b_bundle_id")
      if (
        !isB2BLine ||
        lineProperty(properties, "_sample") ||
        Number(line.product_id) !== Number(productId)
      ) {
        continue
      }
      const variantId = Number(line.variant_id)
      const want = wanted.get(variantId)
      const sameLeadTime =
        want &&
        lineProperty(properties, "Lead time") === want.properties["Lead time"]
      if (want && sameLeadTime && !kept.has(variantId)) {
        kept.add(variantId)
        if (line.quantity !== want.quantity) {
          updates[line.key] = want.quantity
          original[line.key] = line.quantity
          if (want.quantity > line.quantity) {
            raised.set(line.key, { variantId, requested: want.quantity })
          }
        }
      } else {
        updates[line.key] = 0
        removed.push({ id: variantId, quantity: line.quantity, properties })
      }
    }

    if (Object.keys(updates).length > 0) {
      const updated = await updateCartLines(scope, updates)
      const updatedItems = Array.isArray(updated?.items) ? updated.items : []
      for (const [key, { variantId, requested }] of raised) {
        const got = updatedItems.find((item) => item.key === key)
        const quantity = Number(got?.quantity) || 0
        if (quantity < requested) {
          const err = stockShortError(scope, {
            variantId: String(variantId),
            quantity,
          })
          await restoreTouchedLines(scope, original, removed, err)
          throw err
        }
      }
    }

    const toAdd = items.filter((item) => !kept.has(item.id))
    if (toAdd.length > 0) {
      try {
        await addItems(scope, toAdd)
      } catch (err) {
        await restoreTouchedLines(scope, original, removed, err)
        throw err
      }
    }
  }

  /** Puts changed quantities back and re-adds zeroed lines; if that fails, throws a cart-may-have-changed error. */
  const restoreTouchedLines = async (
    scope,
    original,
    removed,
    originalError
  ) => {
    try {
      if (Object.keys(original).length > 0) {
        await updateCartLines(scope, original)
      }
      if (removed.length > 0) {
        await addItems(scope, removed)
      }
    } catch (_restoreError) {
      console.error(originalError)
      const status = scope.querySelector("[data-order-status]")
      throw new Error(status?.dataset.cartChangedText || originalError.message)
    }
  }

  // cart/update.js caps a raised quantity at stock instead of failing like cart/add.js.
  const stockShortError = (scope, { variantId, quantity }) => {
    const template =
      document.querySelector("[data-qty-note-strings]")?.dataset.stockText || ""
    const row = [...scope.querySelectorAll("[data-size-row]")].find(
      (candidate) => candidate.dataset.variantId === variantId
    )
    const label = row ? labelForRow(row) : ""
    const message = template
      ? template.split("[count]").join(String(quantity))
      : "Could not add to order."
    return new Error(label ? `${label}: ${message}` : message)
  }

  const addItems = async (scope, items) => {
    const res = await fetch(
      withCountry(ADD_URL, scope.dataset.checkoutCountry),
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ items }),
      }
    )
    if (!res.ok) {
      throw await cartAddError(res)
    }
    return res.json()
  }

  const applyColor = (scope, color, payload = {}) => {
    for (const btn of scope.querySelectorAll("[data-swatch]")) {
      btn.setAttribute(
        "aria-pressed",
        btn.dataset.swatch === color ? "true" : "false"
      )
    }
    syncSelectedColor(scope, color)
    for (const row of scope.querySelectorAll("[data-size-row]")) {
      if (row.dataset.color) {
        row.hidden = row.dataset.color !== color
      }
    }
    updateMainImage(scope, payload, color)
    recalc(scope)
  }

  const editVariant = (scope, variantId, payload = {}) => {
    const row = scope.querySelector(
      `[data-size-row][data-variant-id="${variantId}"]`
    )
    if (!row) {
      return
    }
    scope.dataset.activeVariantId = row.dataset.variantId
    if (row.dataset.color) {
      applyColor(scope, row.dataset.color, payload)
    } else {
      recalc(scope)
    }
    const input = row.querySelector("[data-qty]")
    input?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    })
    input?.focus({ preventScroll: true })
    input?.select?.()
  }

  const selectedSampleRow = (scope) =>
    rows(scope).find((row) => row.dataset.available === "true") ||
    scope.querySelector(`[data-variant-id="${scope.dataset.sampleVariant}"]`)

  const describeRow = (scope, row, target) => {
    if (!row) {
      return
    }
    target.textContent = ""
    const format = scope.dataset.moneyFormat
    // Keyed, so it drops the echoing row (keeps the first key with a value):
    // a one-option product falls back to `variant.title` in every field.
    const parts = []
    const seen = new Set()
    for (const [label, value] of [
      ["Color", row.dataset.color],
      ["Fit", row.dataset.fit],
      ["Size", row.dataset.size],
    ]) {
      if (value && !seen.has(value)) {
        seen.add(value)
        parts.push([label, value])
      }
    }
    parts.push(["Price", fmtMoney(row.dataset.unitPrice, format)])
    for (const [label, value] of parts) {
      const item = document.createElement("div")
      const dt = document.createElement("dt")
      const dd = document.createElement("dd")
      dt.textContent = label
      dd.textContent = value
      item.append(dt, dd)
      target.append(item)
    }
  }

  const setModal = (modal, open, trigger) => {
    if (!modal) {
      return
    }
    modal.hidden = !open
    if (open) {
      modal._b2bTrigger = trigger || null
      modal.querySelector("button")?.focus()
    } else if (modal._b2bTrigger) {
      // Restore focus to whatever opened the modal.
      modal._b2bTrigger.focus()
      modal._b2bTrigger = null
    }
  }

  const MODAL_SELECTOR =
    "[data-sample-modal], [data-stock-modal], [data-size-chart-modal]"

  const closeOpenModals = (scope) => {
    for (const modal of scope.querySelectorAll(MODAL_SELECTOR)) {
      if (!modal.hidden) {
        setModal(modal, false)
      }
    }
  }

  const openSampleModal = (scope, trigger) => {
    const modal = scope.querySelector("[data-sample-modal]")
    const summary = scope.querySelector("[data-sample-summary]")
    const row = selectedSampleRow(scope)
    if (summary) {
      describeRow(scope, row, summary)
    }
    setModal(modal, true, trigger)
  }

  // The file carries every product, so it is too big to inline; fetched lazily
  // on first open and cached for the rest of the page.
  let sizeChartRequest = null

  const loadSizeCharts = (url) => {
    if (!sizeChartRequest) {
      // Drop the cached promise on failure, so one failed fetch does not make
      // every later open report no measurements.
      sizeChartRequest = fetch(url)
        .then((res) => {
          if (!res.ok) {
            throw new Error(`size charts: ${res.status}`)
          }
          return res.json()
        })
        .catch(() => {
          sizeChartRequest = null
          return {}
        })
    }
    return sizeChartRequest
  }

  const renderSizeChart = (body, status, charts, region, onRegion, refocus) => {
    body.textContent = ""
    status.textContent = ""
    let available = ["eu", "us"].filter((key) => charts?.[key]?.length)
    // Sock charts are byte-identical in both regions; collapse to one so the
    // unit toggle does not promise a conversion that never happens.
    if (
      available.length === 2 &&
      JSON.stringify(charts.eu) === JSON.stringify(charts.us)
    ) {
      available = ["eu"]
    }
    if (!available.length) {
      status.textContent = status.dataset.missingText || ""
      return
    }
    const active = available.includes(region) ? region : available[0]

    if (available.length > 1) {
      const units = document.createElement("div")
      units.className = "size-chart__units"
      for (const key of available) {
        const button = document.createElement("button")
        button.type = "button"
        button.className = "size-chart__unit"
        button.dataset.sizeChartUnit = key
        button.setAttribute("aria-pressed", String(key === active))
        button.textContent =
          key === "us"
            ? body.dataset.unitsImperialText || "Inches"
            : body.dataset.unitsMetricText || "Centimetres"
        units.append(button)
      }
      units.addEventListener("click", (event) => {
        const pick = event.target.closest("[data-size-chart-unit]")
        if (pick) {
          onRegion(pick.dataset.sizeChartUnit)
        }
      })
      body.append(units)
    }

    // Toggling units redraws the body and destroys the clicked button, which
    // would otherwise drop a keyboard user back to the top of the document.
    if (refocus) {
      body.querySelector(`[data-size-chart-unit="${active}"]`)?.focus()
    }

    for (const chart of charts[active]) {
      // Table and its explainer share one block so the gap lands between
      // charts, not between a table and the note that belongs to it.
      const block = document.createElement("div")
      block.className = "size-chart__block"
      const wrap = document.createElement("div")
      wrap.className = "size-chart__table-wrap"
      const table = document.createElement("table")
      const caption = document.createElement("caption")
      caption.textContent = chart.name
      table.append(caption)

      const thead = document.createElement("thead")
      const headRow = document.createElement("tr")
      for (const header of chart.headers) {
        const th = document.createElement("th")
        th.scope = "col"
        th.textContent = header
        headRow.append(th)
      }
      thead.append(headRow)
      table.append(thead)

      const tbody = document.createElement("tbody")
      for (const row of chart.rows) {
        const tr = document.createElement("tr")
        row.forEach((cell, index) => {
          const node = document.createElement(index === 0 ? "th" : "td")
          if (index === 0) {
            node.scope = "row"
          }
          node.textContent = cell
          tr.append(node)
        })
        tbody.append(tr)
      }
      table.append(tbody)
      wrap.append(table)
      block.append(wrap)

      if (chart.explainer) {
        const note = document.createElement("p")
        note.className = "size-chart__explainer"
        note.textContent = chart.explainer
        block.append(note)
      }
      body.append(block)
    }
  }

  const openSizeChartModal = async (scope, trigger, payload) => {
    const modal = scope.querySelector("[data-size-chart-modal]")
    const body = scope.querySelector("[data-size-chart-body]")
    const status = scope.querySelector("[data-size-chart-status]")
    if (!modal || !body || !status) {
      return
    }
    body.textContent = ""
    status.textContent = status.dataset.loadingText || ""
    setModal(modal, true, trigger)
    const all = await loadSizeCharts(payload.sizeChartUrl)
    const charts = all[payload.handle]
    let region = payload.sizeChartRegion === "us" ? "us" : "eu"
    const draw = (refocus) =>
      renderSizeChart(
        body,
        status,
        charts,
        region,
        (next) => {
          region = next
          draw(true)
        },
        refocus
      )
    draw(false)
  }

  const openStockModal = (scope, trigger) => {
    const modal = scope.querySelector("[data-stock-modal]")
    const summary = scope.querySelector("[data-stock-summary]")
    if (summary) {
      summary.textContent = ""
      const visibleRows = rows(scope)
      for (const row of visibleRows) {
        const label = uniqueLabelParts([
          row.dataset.fit,
          row.dataset.size,
        ]).join(" / ")
        const available = row.dataset.available === "true"
        const stockLimit = stockLimitForRow(row)
        // Availability decides first: `variant.available` is what checkout
        // enforces; the stock number is only a metafield that refines it.
        const stock = !available
          ? "Out of stock"
          : stockLimit !== null
            ? `${stockUnitLabel(stockLimit)} in ${leadTimeFromScope(scope).text.toLowerCase()}; higher quantities ${leadTimeFromScope(scope, "extended").text.toLowerCase()}`
            : "Available"
        const item = document.createElement("div")
        const name = document.createElement("span")
        const value = document.createElement("strong")
        item.className = "stock-summary__row"
        name.textContent = label || "Variant"
        value.textContent = stock
        item.append(name, value)
        summary.append(item)
      }
    }
    setModal(modal, true, trigger)
  }

  const init = (scope) => {
    const payload = parseJson(scope.querySelector("[data-product-payload]"), {})
    scope.dataset.stockMode = payload.stockMode || "availability"
    initSwatches(scope)
    sortSizeRows(scope)
    syncSizeLabels(scope)
    resetQuantities(scope)
    const restoredRow = restoreStoredQuantities(scope, payload)
    if (restoredRow) {
      scope.dataset.activeVariantId = restoredRow.dataset.variantId
    }
    const firstSwatch = scope.querySelector("[data-swatch]")
    // Honour the server-selected variant (cart-edit deep links echo it onto
    // data-sample-variant), so its colour opens instead of the first swatch.
    const sampleRow = scope.dataset.sampleVariant
      ? scope.querySelector(
          `[data-variant-id="${scope.dataset.sampleVariant}"]`
        )
      : null
    const initialColor =
      restoredRow?.dataset.color ||
      sampleRow?.dataset.color ||
      firstSwatch?.dataset.swatch
    if (initialColor) {
      applyColor(scope, initialColor, payload)
    } else {
      recalc(scope)
    }

    // Fire and forget: the matrix is usable the moment it renders and fills in
    // when the cart answers. A failure is silent on purpose, see the helper.
    void hydrateFromCart(scope, payload)

    const onQtyEvent = (event) => {
      if (event.target.matches("[data-qty]")) {
        // Only a trusted event marks the row: the clamp re-dispatches a
        // synthetic `input`, which is not the buyer claiming the row.
        if (event.isTrusted) {
          event.target.dataset.userEdited = "1"
        }
        const row = event.target.closest("[data-size-row]")
        if (row) {
          scope.dataset.activeVariantId = row.dataset.variantId
        }
        writeStoredQuantities(scope, payload)
        recalc(scope)
      }
    }

    // Listen on `input` only: the clamp re-dispatches `input`, so a `change`
    // binding would double-run recalc from the pre-clamp value.
    scope.addEventListener("input", onQtyEvent)
    scope.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        closeOpenModals(scope)
      }
    })
    scope.addEventListener("focusin", (event) => {
      if (event.target.matches("[data-qty]")) {
        const row = event.target.closest("[data-size-row]")
        if (row) {
          scope.dataset.activeVariantId = row.dataset.variantId
          syncStockAlert(scope)
        }
      }
    })
    scope.addEventListener("click", async (event) => {
      const status = scope.querySelector("[data-order-status]")
      const swatch = event.target.closest("[data-swatch]")
      if (swatch) {
        applyColor(scope, swatch.dataset.swatch, payload)
        return
      }
      const sizeChartTrigger = event.target.closest("[data-open-size-chart]")
      if (sizeChartTrigger) {
        await openSizeChartModal(scope, sizeChartTrigger, payload)
        return
      }
      const stockTrigger = event.target.closest("[data-open-stock]")
      if (stockTrigger) {
        openStockModal(scope, stockTrigger)
        return
      }
      const editButton = event.target.closest("[data-edit-variant]")
      if (editButton) {
        editVariant(scope, editButton.dataset.editVariant, payload)
        return
      }
      const sampleTrigger = event.target.closest("[data-buy-sample]")
      if (sampleTrigger) {
        openSampleModal(scope, sampleTrigger)
        return
      }
      const closeTrigger = event.target.closest("[data-modal-close]")
      const backdrop = event.target.matches(MODAL_SELECTOR)
        ? event.target
        : null
      if (closeTrigger || backdrop) {
        setModal(backdrop || closeTrigger.closest(MODAL_SELECTOR), false)
        return
      }
      if (event.target.closest("[data-add-order]")) {
        if (submitting.has(scope)) {
          return
        }
        recalc(scope)
        const items = collectItems(scope, bundleId())
        if (!items.length) {
          return
        }
        submitting.add(scope)
        recalc(scope)
        setOrderStatus(status, "Adding to order...", null)
        try {
          await upsertProductLines(scope, payload.id, items)
          clearStoredQuantities(payload)
          setOrderStatus(status, "Added. Opening cart...", "ok")
          window.location.href = withCountry(
            CART_URL,
            scope.dataset.checkoutCountry
          )
        } catch (err) {
          submitting.delete(scope)
          recalc(scope)
          setOrderStatus(status, err.message, "error")
        }
        return
      }
      if (event.target.closest("[data-confirm-sample]")) {
        const row = selectedSampleRow(scope)
        const id = row?.dataset.variantId || scope.dataset.sampleVariant
        if (!id) {
          return
        }
        const button = event.target.closest("[data-confirm-sample]")
        if (button) {
          button.disabled = true
        }
        setOrderStatus(status, "Adding sample...", null)
        try {
          await addItems(scope, [
            {
              id: Number(id),
              quantity: 1,
              // `_sample` keeps it out of the ladder: a sample is one unit at
              // list price and must not count toward the tier threshold.
              properties: {
                ...baseProperties(scope, bundleId()),
                _sample: "1",
              },
            },
          ])
          setOrderStatus(status, "Sample added. Opening cart...", "ok")
          window.location.href = withCountry(
            CART_URL,
            scope.dataset.checkoutCountry
          )
        } catch (err) {
          // Re-enabled only on failure: the success path is navigating to the
          // cart, and a live button during that wait adds a second sample.
          if (button) {
            button.disabled = false
          }
          setOrderStatus(status, err.message, "error")
        }
      }
    })
  }

  const initAll = (root) => {
    for (const scope of root.querySelectorAll("[data-product-form]")) {
      init(scope)
    }
  }

  initAll(document)

  // Theme editor: re-bind behavior after a section re-render.
  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target)
  })

  // A bfcache restore replays the DOM from when the cart opened, submit guard
  // still set and buttons still disabled, so release both or they come back dead.
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) {
      return
    }
    for (const scope of document.querySelectorAll("[data-product-form]")) {
      submitting.delete(scope)
      const sample = scope.querySelector("[data-confirm-sample]")
      if (sample) {
        sample.disabled = false
      }
      recalc(scope)
    }
  })
})()
